import React, { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { SubmissionReader } from '../../types/submission';

interface ShareAccessManagerProps {
  submissionId: string;
}

interface NoticeState {
  kind: 'success' | 'error' | 'info';
  message: string;
}

const formatShareErrorMessage = (
  message: string | undefined,
  fallbackMessage: string,
): string => {
  if (!message) {
    return fallbackMessage;
  }

  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes('no timely user exists with that email')) {
    return 'No Timely account exists for that email.';
  }

  if (normalizedMessage.includes('submission owners already have access')) {
    return 'The submission owner already has access.';
  }

  if (normalizedMessage.includes('only admins can manage shared access')) {
    return 'Only admins can add or remove shared readers.';
  }

  if (normalizedMessage.includes('submission not found')) {
    return 'That submission could not be found.';
  }

  return message;
};

const ShareAccessManager: React.FC<ShareAccessManagerProps> = ({ submissionId }) => {
  const supabase = useSupabase();
  const [readers, setReaders] = useState<SubmissionReader[]>([]);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingReaderId, setRemovingReaderId] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const fetchReaders = useCallback(async () => {
    setIsLoading(true);

    const { data, error } = await supabase.rpc('list_submission_readers', {
      p_submission_id: submissionId,
    });

    if (error) {
      setNotice({
        kind: 'error',
        message: formatShareErrorMessage(error.message, 'Unable to load shared access details.'),
      });
      setReaders([]);
      setIsLoading(false);
      return;
    }

    setReaders((data || []) as SubmissionReader[]);
    setIsLoading(false);
  }, [submissionId, supabase]);

  useEffect(() => {
    fetchReaders();
  }, [fetchReaders]);

  const handleAddReader = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setNotice({
        kind: 'error',
        message: 'Enter an email address to share this submission.',
      });
      return;
    }

    setIsSubmitting(true);
    setNotice(null);

    const { data, error } = await supabase.rpc('add_submission_reader', {
      p_submission_id: submissionId,
      p_reader_email: normalizedEmail,
    });

    if (error) {
      setNotice({
        kind: 'error',
        message: formatShareErrorMessage(error.message, 'Unable to grant shared access.'),
      });
      setIsSubmitting(false);
      return;
    }

    const addedReader = data?.[0];
    if (!addedReader) {
      setNotice({
        kind: 'error',
        message: 'Unable to confirm shared access for that user.',
      });
      setIsSubmitting(false);
      return;
    }

    setEmail('');
    let noticeKind: NoticeState['kind'] = addedReader.was_created ? 'success' : 'info';
    let noticeMessage = addedReader.was_created
      ? `Read-only access granted to ${normalizedEmail}.`
      : `${normalizedEmail} already has read-only access.`;

    if (addedReader.was_created) {
      const { error: emailError } = await supabase.functions.invoke('submission_share_notification_email', {
        body: {
          submission_id: submissionId,
          reader_user_id: addedReader.reader_user_id,
        },
      });

      if (emailError) {
        console.error('Error sending shared access notification email:', emailError);
        noticeKind = 'info';
        noticeMessage = `Read-only access granted to ${normalizedEmail}, but the notification email could not be sent.`;
      }
    }

    setNotice({
      kind: noticeKind,
      message: noticeMessage,
    });
    await fetchReaders();
    setIsSubmitting(false);
  };

  const handleRemoveReader = async (readerUserId: string, readerEmail: string) => {
    setRemovingReaderId(readerUserId);
    setNotice(null);

    const { data, error } = await supabase.rpc('remove_submission_reader', {
      p_submission_id: submissionId,
      p_reader_user_id: readerUserId,
    });

    if (error) {
      setNotice({
        kind: 'error',
        message: formatShareErrorMessage(error.message, 'Unable to remove shared access.'),
      });
      setRemovingReaderId(null);
      return;
    }

    if (!data) {
      setNotice({
        kind: 'info',
        message: 'That shared user no longer has access.',
      });
      setRemovingReaderId(null);
      await fetchReaders();
      return;
    }

    setNotice({
      kind: 'success',
      message: `Removed read-only access for ${readerEmail}.`,
    });
    await fetchReaders();
    setRemovingReaderId(null);
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Manage Shared Read Access</h2>
        <p className="text-sm text-gray-600">
          Admins can grant read-only access to existing Timely users by email. Shared users can review submission details, documents, and billing, but they cannot edit.
        </p>
      </div>

      <form className="flex flex-col gap-3 md:flex-row" onSubmit={handleAddReader}>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="user@example.com"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#F55D3E] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-[#F55D3E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d94a30] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? 'Sharing...' : 'Add Reader'}
        </button>
      </form>

      {notice && (
        <div
          className={`mt-4 rounded-md border px-4 py-3 text-sm ${
            notice.kind === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : notice.kind === 'info'
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="mt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Current Readers</h3>
        {isLoading ? (
          <p className="mt-3 text-sm text-gray-500">Loading shared users...</p>
        ) : readers.length === 0 ? (
          <p className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
            This submission has not been shared yet.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200">
            {readers.map((reader) => (
              <li key={reader.reader_user_id} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {reader.first_name} {reader.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{reader.email}</p>
                  <p className="text-xs text-gray-500">
                    {reader.organization} • Added {new Date(reader.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveReader(reader.reader_user_id, reader.email)}
                  disabled={removingReaderId === reader.reader_user_id}
                  className="self-start rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 md:self-center"
                >
                  {removingReaderId === reader.reader_user_id ? 'Removing...' : 'Remove Access'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ShareAccessManager;
